import { ChangeDetectionStrategy, Component, input } from '@angular/core';

// Umbenannt aus `ResultStatus`, damit der Import nicht mit dem gleichnamigen
// Domaenentyp `ResultStatus` aus models/test-run.model kollidiert
// (NEX-1085 Review, sgraetz W1). `not-run` hat im Bundle-R_PILL keine Entsprechung
// (page-testresultate.jsx:42-48 kennt nur fuenf Varianten). Farben deshalb aus den
// neutralen `--to-gray-*`-Tokens abgeleitet, damit ein noch nie ausgefuehrter
// Testfall visuell klar von "Uebersprungen" trennt (sgraetz-Nachfassung 4).
export type ResPillStatus = 'passed' | 'failed' | 'running' | 'blocked' | 'skipped' | 'not-run' | 'waiting';

@Component({
  selector: 'app-res-pill',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      class="res-pill"
      [class.res-pill--pulsing]="pulsing()"
      [class.res-pill--sm]="size() === 'sm'"
      [attr.data-status]="status()"
      [attr.data-size]="size()"
      data-testid="res-pill"
    >
      <span class="res-pill__dot" aria-hidden="true"></span>
      {{ label() }}
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .res-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.4375rem;
      padding: 0.3125rem 0.6875rem;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;
      background: var(--to-slate-bg);
      color: var(--to-slate-fg);

      &[data-status='passed'] {
        background: var(--to-green-bg);
        color: var(--to-green-fg);
      }
      &[data-status='failed'] {
        background: var(--to-red-bg);
        color: var(--to-red-fg);
      }
      &[data-status='running'] {
        background: var(--to-blue-bg);
        color: var(--to-blue-fg);
      }
      &[data-status='blocked'] {
        background: var(--to-warn-bg);
        color: var(--to-warn-fg);
      }
      &[data-status='not-run'] {
        background: var(--to-gray-bg);
        color: var(--to-text-muted);
      }
      // Wartender Fall im Rechner-Lauf (page-abgleich-bloecke.jsx:63).
      &[data-status='waiting'] {
        background: var(--to-neutral-bg);
        color: var(--to-text-muted);
      }
    }

    .res-pill--pulsing {
      font-weight: 700;
    }

    .res-pill--pulsing .res-pill__dot {
      width: 0.5rem;
      height: 0.5rem;
      animation: pulse 1.1s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      .res-pill--pulsing .res-pill__dot {
        animation: none;
      }
    }

    .res-pill__dot {
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 50%;
      flex-shrink: 0;
      background: var(--to-slate-dot-soft);

      .res-pill[data-status='passed'] & {
        background: var(--to-green-dot);
      }
      .res-pill[data-status='failed'] & {
        background: var(--to-red-dot);
      }
      .res-pill[data-status='running'] & {
        background: var(--to-blue-dot);
      }
      .res-pill[data-status='blocked'] & {
        background: var(--to-warn-dot);
      }
      .res-pill[data-status='not-run'] & {
        background: var(--to-gray-dot);
      }
    }

    // RfPill der Release-Freigabe (page-release-freigabe.jsx:53-62): Punkt in Textfarbe,
    // "Nicht ausgefuehrt" in Slate wie RF_PILL.pending.
    .res-pill--sm {
      gap: 0.375rem;
      padding: 0.1875rem 0.625rem;

      &[data-status='not-run'] {
        background: var(--to-slate-bg);
        color: var(--to-slate-fg);
      }

      &[data-status] .res-pill__dot {
        background: currentColor;
      }
    }
  `,
})
export class ResPillComponent {
  readonly status = input.required<ResPillStatus>();
  readonly label = input.required<string>();

  /**
   * Laufender Durchlauf: kraeftigerer Punkt mit Puls (NEX-1037). Die Animation
   * entfaellt bei `prefers-reduced-motion: reduce`.
   */
  readonly pulsing = input<boolean>(false);

  readonly size = input<'md' | 'sm'>('md');
}
